<script setup lang="ts">
  import type { HTMLAttributes } from 'vue'
  import { cn } from '../../../lib/utils'
  import { ref, watch, useSlots } from 'vue'
  import { inputVariants, type InputVariants } from '.'
  import { Tooltip } from '../tooltip'
  import Icon from '../icon/Icon.vue'

  const modelValue = defineModel<string | number>({ default: '' })

  const props = defineProps<{
    class?: HTMLAttributes['class']
    size?: InputVariants['size']
    disabled?: boolean
    /**
     * 验证函数
     * @param value 当前输入值
     * @returns boolean | string | Promise<boolean | string>
     * 返回 true 表示验证通过
     * 返回 false 或 错误信息字符串 表示验证失败
     */
    validate?: (value: string | number) => boolean | string | Promise<boolean | string>
    /** 外部控制的错误状态 */
    error?: boolean
    /** 外部传入的错误信息 */
    errorMessage?: string
    /** Tooltip 提示内容 */
    tooltip?: string
  }>()

  const emits = defineEmits<{
    (e: 'update:error', payload: boolean): void
    (e: 'blur', event: FocusEvent): void
    /** 验证完成事件，payload 为验证结果（是否通过） */
    (e: 'validate', payload: boolean): void
  }>()

  const slots = useSlots()

  const internalError = ref(false)
  const internalErrorMessage = ref('')

  // 监听外部 error 变化，同步内部状态
  watch(
    [() => props.error, () => props.errorMessage],
    ([newError, newErrorMessage]) => {
      if (newErrorMessage) {
        internalError.value = true
        internalErrorMessage.value = newErrorMessage
      } else if (newError !== undefined) {
        internalError.value = newError
        if (!newError) {
          internalErrorMessage.value = ''
        }
      } else {
        internalError.value = false
        internalErrorMessage.value = ''
      }
    },
    { immediate: true },
  )

  const handleBlur = async (event?: FocusEvent) => {
    if (event) {
      emits('blur', event)
    }

    if (props.validate) {
      try {
        const result = await props.validate(modelValue.value || '')
        // 如果返回 true，表示验证通过 (isError = false)
        // 如果返回 false 或 字符串，表示验证失败 (isError = true)
        const isError = result === false || typeof result === 'string'

        // 更新错误信息
        if (typeof result === 'string') {
          internalErrorMessage.value = result
        } else if (isError) {
          // 如果返回 false 但没有具体信息，不显示文字只变红
          internalErrorMessage.value = ''
        } else {
          internalErrorMessage.value = ''
        }

        internalError.value = isError
        emits('update:error', isError)
        emits('validate', !isError)
        return !isError
      } catch (e) {
        console.error('Validation error:', e)
        // 发生异常视为验证失败
        internalError.value = true
        internalErrorMessage.value = 'validation error'
        emits('update:error', true)
        emits('validate', false)
        return false
      }
    }
    return true
  }

  // 暴露 validate 方法给父组件
  defineExpose({
    validate: () => handleBlur(),
  })
  defineOptions({ inheritAttrs: false })
</script>

<template>
  <div class="w-full">
    <div class="relative flex items-center w-full">
      <Tooltip :content="tooltip" :disabled="!tooltip" side="bottom">
        <div class="flex items-center w-full relative">
          <!-- 前缀图标 -->
          <div
            v-if="slots.prefix"
            class="absolute left-3 flex items-center justify-center text-muted-foreground pointer-events-none"
          >
            <slot name="prefix" />
          </div>

          <input
            v-model="modelValue"
            :disabled="props.disabled"
            :class="
              cn(
                inputVariants({ size }),
                internalError && 'ring-1 ring-state-failure focus-visible:ring-state-failure',
                slots.prefix && 'pl-9',
                slots.suffix && 'pr-9',
                props.class,
              )
            "
            v-bind="$attrs"
            @blur="handleBlur"
          />

          <!-- 后缀图标 -->
          <div
            v-if="slots.suffix"
            class="absolute right-3 flex items-center justify-center text-muted-foreground pointer-events-none"
          >
            <slot name="suffix" />
          </div>
        </div>
      </Tooltip>
    </div>

    <div
      v-if="internalErrorMessage && internalError"
      class="text-brand-red text-s-medium flex items-center gap-1 mt-1"
    >
      <Icon icon="Function-Fill-Close" class="w-4 h-4" />
      {{ internalErrorMessage }}
    </div>
  </div>
</template>

<style scoped>
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
</style>
